import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";
import { useCompanyFilterOptions } from "../common/hooks/useCompanyFilterOptions";
import { Pagination } from "../general/pagination.component";
import { StaffProfilePanel } from "../staff/components/profilePanel/profilePanel.component";
import { MonthSalaryBody } from "./salary.styled";
import { SalaryListRow } from "./salaryListRow.component";
import {
  ColumnListWrapper,
  EmptyState,
} from "../applications/applicationList.styled";

type Props = {
  selectedWorker?: number;
};

export const SalaryList: React.FC<Props> = ({ selectedWorker }) => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages }] = useCompanyStore();
  const [state, monthSalaryActions] = useMonthSalaryStore();

  const [page, setPage] = useState(0);
  const [selectedSalaryWorkerId, setSelectedSalaryWorkerId] = useState<
    number | null
  >(null);

  const filterOptions = useCompanyFilterOptions({ companyId: company?.id });

  const pageSize = 25;

  useEffect(() => {
    if (!company?.id) return;
    monthSalaryActions.getAllSalaries(company.id, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, page]);

  const displayedWorkers = selectedWorker
    ? state.workersWithSalary.filter((w) => w.workerId === selectedWorker)
    : state.workersWithSalary;

  const totalPages = Math.ceil(state.total / pageSize);

  const handleSelectWorker = (workerId: number): void => {
    setSelectedSalaryWorkerId(workerId);
  };

  const handleCloseSalaryPanel = (): void => {
    setSelectedSalaryWorkerId(null);
  };

  const handleSalaryPanelSaved = (): void => {
    if (!company?.id) return;
    monthSalaryActions.getAllSalaries(company.id, page, pageSize);
  };

  const renderSalaries = (): JSX.Element => {
    if (displayedWorkers.length === 0) {
      return <EmptyState typography="b1">{t("Salary.NoSalaries")}</EmptyState>;
    }

    return (
      <MonthSalaryBody>
        {displayedWorkers.map((salary) => (
          <SalaryListRow
            key={`worker-${salary.workerId}`}
            salary={salary}
            avatarUrl={
              salary.externalId
                ? companyWorkerImages[salary.externalId]
                : undefined
            }
            onSelectWorker={handleSelectWorker}
          />
        ))}
      </MonthSalaryBody>
    );
  };

  return (
    <>
      <ColumnListWrapper>
        {renderSalaries()}
        {totalPages > 1 && (
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        )}
      </ColumnListWrapper>

      <StaffProfilePanel
        companyId={company?.id ?? null}
        workerId={selectedSalaryWorkerId}
        unitTeamOptions={filterOptions.unitTeamOptions}
        isOpen={selectedSalaryWorkerId != null}
        initialTab="employmentSalary"
        onClose={handleCloseSalaryPanel}
        onSaved={handleSalaryPanelSaved}
      />
    </>
  );
};
